import React from 'react';
import { Box, Typography, LinearProgress, Button } from '@mui/material';
import CheckIcon from '@material-ui/icons/Check';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import { useColorMode } from '../../../../../context/colorMode/colorMode.context';

const DEFAULT_CARDS = [
    { title: 'Learning Goals',              items: ['Core Learning Objectives', "Bloom's Taxonomy Levels 2-4"] },
    /* Slide 7 — never name a framework the program has not been aligned to.
       ProgramPreview passes the real list; this default only shows while
       that is still unknown. */
    { title: 'Framework Alignment',         items: ['No external framework', 'Aligning to DQ standards only'] },
    { title: 'Lesson Content + Activities', items: ['Intro Video', 'Slides', 'Group Activity', 'Reflection'] },
    { title: 'Pre + Post Assessment',       items: ['Questions', 'Scoring Preview*', 'Impact Data'] },
    { title: 'Impact Dashboard',            items: ['Before/After Scores', 'Class Benchmarks', 'Reports'] },
];

const ORANGE = '#FF6900';

/* Slide 8 spells the count out ("Five parts built and aligned"). Only the
   plausible lengths are worth naming; anything else falls back to digits. */
const COUNT_WORD = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight'];

const BuildingProgramView = ({
    activeCardIndex = 2,
    activeCardProgress = 0,
    cards = DEFAULT_CARDS,
    isComplete = false,
    onPreviewClick,
}) => {
    const { isDark } = useColorMode();
    const effCardIdx = isComplete ? cards.length : activeCardIndex;

    return (
        <Box sx={{
            width: '100%',
            minHeight: '100%',
            p: { xs: 2, md: 4 },
            backgroundColor: isDark ? '#121212' : '#FAF8F4',
            fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
        }}>
            <style>{`
                @keyframes bpvShimmerBar {
                    0%   { background-position: 100% 0; }
                    100% { background-position: -100% 0; }
                }
                @keyframes bpvBorderPulse {
                    0%, 100% { box-shadow: 0 0 0 0px rgba(255,105,0,0.2), 0 12px 28px rgba(0,0,0,0.05); }
                    50%       { box-shadow: 0 0 0 6px rgba(255,105,0,0.1), 0 16px 36px rgba(255,105,0,0.1); }
                }
                @keyframes bpvIconPulse {
                    0%, 100% { transform: scale(1);   opacity: 1; }
                    50%       { transform: scale(1.15); opacity: 0.8; }
                }
            `}</style>

            {/* ── Main heading or completion CTA ────────────────────────────── */}
            <Box sx={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', gap: { xs: '12px', md: '18px' },
                mb: { xs: 2, md: 4 },
            }}>
                {isComplete && (
                    /* Slide 8: name what was finished before offering the button. */
                    <Typography sx={{
                        fontSize: { xs: 15, md: 18 },
                        fontWeight: 500,
                        color: 'var(--brand-text, #1a1a1a)',
                        textAlign: 'center',
                        lineHeight: 1.5,
                    }}>
                        {`${COUNT_WORD[cards.length] || cards.length} parts built and aligned. Preview it end to end before you publish.`}
                    </Typography>
                )}
                {isComplete ? (
                    <Button
                        onClick={onPreviewClick}
                        sx={{
                            // Slide 8: "Change gradient scheme to be solid."
                            background: '#FF6900 !important',
                            color: '#fff',
                            fontSize: { xs: 14, md: 16 },
                            fontWeight: 500,
                            textTransform: 'none',
                            borderRadius: '14px',
                            padding: { xs: '12px 28px', md: '16px 48px' },
                            lineHeight: 1.3,
                            boxShadow: '0 4px 20px rgba(255,105,0,0.3)',
                            // Eased, and a smaller step: the un-transitioned 1.06 snapped
                            // open the instant the pointer touched it, which read as a jolt.
                            transition: 'transform 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease',
                            '&:hover': {
                                transform: 'scale(1.03)',
                                background: '#E55E00 !important',
                                boxShadow: '0 6px 18px rgba(243, 96, 51, 0.35) !important',
                            },
                        }}
                    >
                        Preview learning program
                    </Button>
                ) : (
                    <Typography sx={{ fontSize: { xs: 18, md: 26 }, fontWeight: 700, color: 'var(--brand-text, #1a1a1a)', textAlign: 'center' }}>
                        Building Your Aligned Learning Program
                    </Typography>
                )}
            </Box>

            {/* ── 5 cards row ───────────────────────────────────────────────── */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(5, 1fr)',
                gap: 2.5,
                mb: 4,
                maxWidth: '1300px',
                width: '100%',
                mx: 'auto',
                '@media (max-width: 1100px)': { gridTemplateColumns: 'repeat(3, 1fr)' },
                '@media (max-width: 750px)':  { gridTemplateColumns: 'repeat(2, 1fr)' },
                '@media (max-width: 500px)':  { gridTemplateColumns: '1fr' },
            }}>
                {cards.map((card, idx) => {
                    const isDone = idx < effCardIdx;
                    const isActive = idx === effCardIdx;
                    const isUpcoming = idx > effCardIdx;

                    const cardBg = isUpcoming
                        ? (isDark ? 'rgba(255,255,255,0.04)' : '#F5F5F7')
                        : (isDark ? '#1E1E1E' : '#fff');

                    const cardBorder = isActive
                        ? `2px solid ${ORANGE}`
                        : isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #E0E0E0';

                    return (
                        <Box key={idx} sx={{
                            background: cardBg,
                            border: cardBorder,
                            borderRadius: '20px',
                            padding: { xs: 2, md: 3 },
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 0,
                            minHeight: { xs: 'auto', md: 340 },
                            boxShadow: isUpcoming ? 'none' : isDark ? '0 4px 12px rgba(0,0,0,0.3)' : '0 10px 20px rgba(0,0,0,0.04)',
                            opacity: isUpcoming ? (isDark ? 0.45 : 0.6) : 1,
                            transition: 'all 0.3s ease',
                            textAlign: 'center',
                            position: 'relative',
                            overflow: 'hidden',
                            ...(isActive && { animation: 'bpvBorderPulse 3s ease-in-out infinite' }),
                        }}>
                            {/* Status icon */}
                            <Box sx={{ mb: { xs: 1.5, md: 3 }, position: 'relative', zIndex: 1 }}>
                                {isDone && (
                                    <Box sx={{
                                        width: 44, height: 44, borderRadius: '12px',
                                        background: isDark ? 'rgba(76,175,80,0.18)' : '#E0F5E1',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <CheckIcon style={{ color: '#4CAF50', fontSize: 24 }} />
                                    </Box>
                                )}
                                {isActive && (
                                    <Box sx={{
                                        width: 44, height: 44, borderRadius: '12px',
                                        background: isDark ? 'rgba(255,105,0,0.18)' : '#FDEEE9',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        animation: 'bpvIconPulse 1.8s ease-in-out infinite',
                                    }}>
                                        <AccessTimeIcon style={{ color: ORANGE, fontSize: 24 }} />
                                    </Box>
                                )}
                                {isUpcoming && (
                                    <Box sx={{
                                        width: 44, height: 44, borderRadius: '12px',
                                        background: isDark ? 'rgba(255,255,255,0.08)' : '#EAEAEA',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <AccessTimeIcon style={{ color: isDark ? 'rgba(255,255,255,0.3)' : '#AAA', fontSize: 24 }} />
                                    </Box>
                                )}
                            </Box>

                            {/* Title */}
                            <Box sx={{ minHeight: { xs: 'auto', md: 50 }, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: { xs: 1, md: 2 }, position: 'relative', zIndex: 1 }}>
                                <Typography sx={{
                                    fontSize: 16, fontWeight: 700,
                                    color: isUpcoming
                                        ? (isDark ? 'rgba(255,255,255,0.28)' : '#999')
                                        : 'var(--brand-text, #1a1a1a)',
                                    lineHeight: 1.3,
                                    px: 1,
                                }}>
                                    {card.title}
                                </Typography>
                            </Box>

                            {/* Items */}
                            <Box
                                component="ul"
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.8,
                                    m: 0,
                                    pl: 3,
                                    textAlign: 'left',
                                    alignSelf: 'flex-start',
                                    width: '100%',
                                    mb: 2,
                                    position: 'relative',
                                    zIndex: 1,
                                }}
                            >
                                {card.items.map((item, i) => (
                                    <Typography
                                        key={i}
                                        component="li"
                                        sx={{
                                            fontSize: 13,
                                            fontWeight: 500,
                                            color: isUpcoming
                                                ? (isDark ? 'rgba(255,255,255,0.22)' : '#AAA')
                                                : 'var(--brand-text-muted, #555)',
                                            lineHeight: 1.3,
                                            /*
                                             * A framework code has no spaces, so it could
                                             * not wrap and ran outside the card. Break it
                                             * anywhere; min-width: 0 stops one long word
                                             * forcing the column wider than the card.
                                             */
                                            overflowWrap: 'anywhere',
                                            minWidth: 0,
                                            '&::marker': {
                                                color: isUpcoming
                                                    ? (isDark ? 'rgba(255,255,255,0.18)' : '#CCC')
                                                    : ORANGE,
                                                fontSize: '1.2em',
                                            },
                                        }}
                                    >
                                        {item}
                                    </Typography>
                                ))}
                            </Box>

                            {/* Progress bar */}
                            <Box sx={{ width: '100%', mt: 'auto', position: 'relative', zIndex: 1 }}>
                                {isDone && (
                                    <>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#4CAF50', mb: 0.5 }}>
                                            100%
                                        </Typography>
                                        <LinearProgress
                                            variant="determinate"
                                            value={100}
                                            sx={{
                                                height: 6, borderRadius: 4,
                                                backgroundColor: isDark ? 'rgba(76,175,80,0.18)' : '#E0F5E1',
                                                '& .MuiLinearProgress-bar': { backgroundColor: '#4CAF50' },
                                            }}
                                        />
                                    </>
                                )}
                                {isActive && (
                                    <>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: ORANGE, mb: 0.5 }}>
                                            {activeCardProgress}%
                                        </Typography>
                                        <LinearProgress
                                            variant="determinate"
                                            value={activeCardProgress}
                                            sx={{
                                                height: 7, borderRadius: 4,
                                                backgroundColor: isDark ? 'rgba(255,105,0,0.18)' : '#FAE0D8',
                                                '& .MuiLinearProgress-bar': {
                                                    background: `linear-gradient(90deg, ${ORANGE} 25%, #ffcfa3 50%, ${ORANGE} 75%)`,
                                                    backgroundSize: '400% 100%',
                                                    animation: 'bpvShimmerBar 1.4s linear infinite',
                                                    borderRadius: 4,
                                                },
                                            }}
                                        />
                                    </>
                                )}
                                {isUpcoming && (
                                    <>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: isDark ? 'rgba(255,255,255,0.2)' : '#CCC', mb: 0.5 }}>
                                            0%
                                        </Typography>
                                        <LinearProgress
                                            variant="determinate"
                                            value={0}
                                            sx={{
                                                height: 6, borderRadius: 4,
                                                backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : '#EAEAEA',
                                                '& .MuiLinearProgress-bar': { backgroundColor: isDark ? 'rgba(255,255,255,0.15)' : '#CCC' },
                                            }}
                                        />
                                    </>
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            {/* ── Footer note ───────────────────────────────────────────────── */}
            <Typography sx={{
                fontSize: 13,
                color: 'var(--brand-text-muted, #555)',
                textAlign: 'center',
                maxWidth: 540,
                mx: 'auto',
                lineHeight: 1.5,
            }}>
                <strong>* Preview scores are not saved.</strong> Once your program is live, real learner scores will appear in your impact dashboard automatically.
            </Typography>
        </Box>
    );
};

export default BuildingProgramView;
